import { z } from 'zod'

import { type AppSupabaseClient } from '@/platform/supabase/client'
import { callRpc } from '@/platform/supabase/rpc'
import { ERROR_CODE } from '@/shared/errors/codes'
import { AppError } from '@/shared/errors/envelope'

import {
  lessonSummarySchema,
  type SessionDelivery,
  sessionDeliverySchema,
  sessionSummarySchema,
  submissionResultProblems,
  submitItemInputSchema,
  submitItemResponseSchema,
  summaryProblems,
} from './contract'
import { type LearningService } from './service'

/**
 * The real learning service: the four First Learning Journey RPCs in
 * `supabase/migrations/20261101000001_first_learning_journey.sql`
 * (docs/design/first-learning-journey-v1.0.md).
 *
 * The backend owns identity checks, grading, duplicate handling and objective
 * observations. This client only validates: inputs before they are sent, and
 * every response against the strict contract schemas. It never grades and
 * never falls back to the MOCK service. A response claiming `is_mock: true`
 * is rejected: real attempts are never presented as simulated, and simulated
 * results never as real.
 */

const uuidSchema = z.uuid()

function requireUuids(...values: string[]): void {
  if (!values.every((v) => uuidSchema.safeParse(v).success)) {
    throw new AppError(ERROR_CODE.INVALID_INPUT)
  }
}

function invalidResponse(reason: string): never {
  throw new AppError(ERROR_CODE.INTERNAL_ERROR, { cause: new Error(reason) })
}

export function createSupabaseLearningService(
  client: AppSupabaseClient,
): LearningService {
  // Deliveries of this browser session, so item results and summaries can be
  // checked against the questions they belong to. Not persisted: there is no
  // resume after reload in this slice.
  const deliveries = new Map<string, SessionDelivery>()

  return {
    getTodayLesson: async (learnerId) => {
      requireUuids(learnerId)
      const lesson = await callRpc(
        client,
        'get_today_lesson',
        { p_learner_id: learnerId },
        lessonSummarySchema.nullable(),
      )
      if (lesson?.is_mock === true) invalidResponse('real lesson marked as mock')
      return lesson
    },

    startSession: async (learnerId, lessonId, clientSessionId) => {
      requireUuids(learnerId, lessonId, clientSessionId)
      const delivery = await callRpc(
        client,
        'start_lesson_attempt',
        {
          p_learner_id: learnerId,
          p_lesson_id: lessonId,
          p_client_session_id: clientSessionId,
        },
        sessionDeliverySchema,
      )
      if (delivery.is_mock) invalidResponse('real session marked as mock')
      if (delivery.learner_id !== learnerId || delivery.lesson_id !== lessonId) {
        invalidResponse('session is for another learner or lesson')
      }
      deliveries.set(delivery.session_id, delivery)
      return delivery
    },

    submitItem: async (rawInput) => {
      const parsed = submitItemInputSchema.safeParse(rawInput)
      if (!parsed.success) throw new AppError(ERROR_CODE.INVALID_INPUT)
      const input = parsed.data
      const response = await callRpc(
        client,
        'submit_lesson_answer',
        {
          p_session_id: input.session_id,
          p_item_id: input.item_id,
          p_client_attempt_id: input.client_attempt_id,
          p_response: input.response,
          p_elapsed_ms: input.elapsed_ms,
          p_hinted: input.hinted,
        },
        submitItemResponseSchema,
      )
      const { result } = response
      if (
        result.item_id !== input.item_id ||
        result.client_attempt_id !== input.client_attempt_id
      ) {
        invalidResponse('result is for another submission')
      }
      const delivery = deliveries.get(input.session_id)
      const item = delivery?.items.find((i) => i.item_id === input.item_id)
      if (
        delivery !== undefined &&
        (item === undefined ||
          submissionResultProblems(item, result, delivery.data_class).length > 0)
      ) {
        invalidResponse('result does not fit the delivered item')
      }
      return response
    },

    completeSession: async (sessionId) => {
      requireUuids(sessionId)
      const summary = await callRpc(
        client,
        'complete_lesson_attempt',
        { p_session_id: sessionId },
        sessionSummarySchema,
      )
      if (summary.is_mock || summary.recommended_practice.some((p) => p.is_mock)) {
        invalidResponse('real summary marked as mock')
      }
      const delivery = deliveries.get(sessionId)
      if (
        summary.session_id !== sessionId ||
        (delivery !== undefined && summaryProblems(delivery, summary).length > 0)
      ) {
        invalidResponse('summary does not fit the session')
      }
      return summary
    },
  }
}
